import { ChevronRight, Hash, type LucideIcon, Target } from "lucide-react"
import {
	TaxonomyRail,
	TaxonomyRailSkeleton,
	useNiches,
	usePostTypes,
	useTopics,
} from "@/entities/taxonomy"
import { pluralize } from "@/shared/lib/pluralize"
import { HapticLink } from "@/shared/ui/haptic-link"
import { SectionHeading } from "@/shared/ui/section-heading"
import { Skeleton } from "@/shared/ui/skeleton"

export function FormatsRail() {
	const { data: formats = [], isLoading } = usePostTypes()
	const { data: topics = [], isLoading: topicsLoading } = useTopics()
	const { data: niches = [], isLoading: nichesLoading } = useNiches()

	return (
		<section data-tour="categories">
			<SectionHeading title="Форматы" />
			{isLoading ? (
				<TaxonomyRailSkeleton />
			) : (
				<TaxonomyRail items={formats} getPath={(item) => `/f/${item.id}`} />
			)}

			<div className="mt-4 grid grid-cols-2 gap-2.5">
				<EntryCard
					icon={Hash}
					title="Темы"
					subtitle={topicsLoading ? null : pluralize(topics.length, ["тема", "темы", "тем"])}
					to={topics[0] && `/t/${topics[0].id}`}
				/>
				<EntryCard
					icon={Target}
					title="Ниши"
					subtitle={nichesLoading ? null : pluralize(niches.length, ["ниша", "ниши", "ниш"])}
					to={niches[0] && `/n/${niches[0].id}`}
				/>
			</div>
		</section>
	)
}

const ENTRY =
	"relative flex flex-col items-start gap-2.5 rounded-2xl bg-card p-4 text-left ring-1 ring-white/10 transition-transform duration-150 ease-[var(--ease-out-soft)] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"

function EntryCard({
	icon: Icon,
	title,
	subtitle,
	to,
}: {
	icon: LucideIcon
	title: string
	subtitle: string | null
	to?: string
}) {
	const body = (
		<>
			<Icon className="size-7 text-primary" strokeWidth={2.4} />
			<span className="min-w-0">
				<span className="block truncate text-[15px] leading-tight font-semibold">{title}</span>
				{subtitle === null ? (
					<Skeleton className="mt-1 h-3 w-14" />
				) : (
					<span className="mt-0.5 block truncate text-xs text-muted-foreground">{subtitle}</span>
				)}
			</span>
			<ChevronRight className="absolute top-4 right-3 size-4 text-muted-foreground" />
		</>
	)

	if (!to) {
		return (
			<div aria-disabled className={`${ENTRY} opacity-60`}>
				{body}
			</div>
		)
	}

	return (
		<HapticLink to={to} className={ENTRY}>
			{body}
		</HapticLink>
	)
}
